<template>
  <div class="onboarding-progress-page">
    <div v-if="internalStep === 0" class="stepper-header onboarding-progress-page__header">
      <v-img alt="Step Logo"
             class="step4-image step-logo onboarding-progress-page__image" />
      <div class="reg-titre1 onboarding-progress-page__title"
           v-html="title"></div>
    </div>

    <OnboardingProgressTable v-if="internalStep === 0"
                             class="onboarding-progress-page__table"
                             :progresses="progresses"
                             disabled />
    <div v-if="internalStep === 1" class="onboarding-project-organization">
      <v-img alt="Step Logo"
             class="step4-image step-logo onboarding-project-organization__image" />
      <div class="reg-titre1 onboarding-project-organization__title"
           v-html="projectOrganizationTitle"></div>

      <div class="onboarding-project-organization__cards">
        <button type="button"
                class="onboarding-project-organization-card"
                :class="{ 'onboarding-project-organization-card--selected': projetGroupByNoneSelected }"
                @click="setProjetGroupByNone">
          <span class="onboarding-project-organization-card__radio"
                :class="{ 'onboarding-project-organization-card__radio--selected': projetGroupByNoneSelected }"></span>
          <img class="onboarding-project-organization-card__icon"
               :src="numeroObjetImage"
               alt="" />
          <span class="onboarding-project-organization-card__label">
            {{ noneLabel }}
          </span>
        </button>

        <button type="button"
                class="onboarding-project-organization-card"
                :class="{ 'onboarding-project-organization-card--selected': projetGroupByMonthSelected }"
                @click="setProjetGroupByMonth">
          <span class="onboarding-project-organization-card__radio"
                :class="{ 'onboarding-project-organization-card__radio--selected': projetGroupByMonthSelected }"></span>
          <img class="onboarding-project-organization-card__icon"
               :src="monthImage"
               alt="" />
          <span class="onboarding-project-organization-card__label">
            {{ monthLabel }}
          </span>
        </button>

        <button type="button"
                class="onboarding-project-organization-card"
                :class="{ 'onboarding-project-organization-card--selected': projetGroupByYearSelected }"
                @click="setProjetGroupByYear">
          <span class="onboarding-project-organization-card__radio"
                :class="{ 'onboarding-project-organization-card__radio--selected': projetGroupByYearSelected }"></span>
          <img class="onboarding-project-organization-card__icon"
               :src="yearImage"
               alt="" />
          <span class="onboarding-project-organization-card__label">
            {{ yearLabel }}
          </span>
        </button>
      </div>
    </div>
  </div>

  <div class="action-footer onboarding-progress-page__footer">
    <div v-if="canReportActions" class="onboarding-progress-page__report-actions">
      <loader-button :label="refreshBtnTxt"
                     :outlined="true"
                     :disabled="refreshDisabled"
                     @callback="refreshImport" />
      <loader-button :label="reportBtnTxt"
                     :outlined="true"
                     @callback="showReport" />
    </div>
    <div class="onboarding-progress-page__nav-actions">
      <loader-button :label="precedentBtnTxt"
                     :outlined="true"
                     :disabled="previousDisabled"
                     @callback="previous" />
      <loader-button :label="suivantBtnTxt"
                     :disabled="nextDisabled"
                     @callback="next" />
    </div>
  </div>

  <section-container v-if="dialogOpen && dialogSection"
                     :section="dialogSection"
                     :dialog-open="dialogOpen"
                     @close-dialog="closeDialog" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LoaderButton from "@/framework/base/components/LoaderButton.vue";
import SectionContainer from "@/framework/base/components/SectionContainer.vue";
import {
  AppButtons,
  AppCustoms,
  RessourceAppButton,
  RessourceAppCustom,
} from "@/ressources/globalRessources.ts";
import OnboardingProgressTable from "@/components/Onboarding/OnboardingProgressTable.vue";
import type { OnboardingProgressDisplay } from "@/components/Onboarding/OnboardingProgressDisplay";
import ProgressCardDocument from "@/assets/SVG/ProgressCardDocument.svg";
import NumeroObjetImage from "@/assets/numeroObjet.png";
import MonthImage from "@/assets/moisImage.png";
import YearImage from "@/assets/anneeImage.png";
import Synchro98, { Synchro98ProjetGroupBys } from "@/generated/classes/Synchro98";
import { isProduction } from "@/framework/base/helpers/Environment";
import DialogHubManager from "@/framework/dialog/helpers/DialogHubManager";
import Dialog from "@/extend/classes/Dialog";
import Section from "@/framework/base/classes/Section";
import SectionDialog from "@/custom/sections/SectionDialog.vue";

export default defineComponent({
  name: "SectionOnboardingImport",
  components: {
    LoaderButton,
    OnboardingProgressTable,
    SectionContainer,
  },
  props: {
    synchro98: {
      type: Synchro98,
      required: false,
    },
    initialStep: {
      type: Number,
      default: 0
    },
    selectedProjetGroupBy: {
      type: Number,
      default: Synchro98ProjetGroupBys.NONE
    },
  },
  emits: ["next", "previous", "updateProjetGroupBy", "failed"],
  data() {
    return {
      internalStep: this.initialStep,
      userId: 0,
      dialogRegistered: false,
      unregisterDialog: null as (() => void) | null,
      dialogOpen: false,
      dialogSection: null as Section | null,
    }
  },
  mounted(): void {
    this.startImport(false);
  },
  beforeUnmount(): void {
    this.unregisterDialog?.();
  },
  computed: {
    title(): string {
      return RessourceAppCustom(AppCustoms.ONBOARDING_ETAPE2_LABEL).label;
    },
    suivantBtnTxt(): string {
      return RessourceAppButton(AppButtons.SUIVANT).label;
    },
    precedentBtnTxt(): string {
      return RessourceAppButton(AppButtons.PRECEDENT).label;
    },
    refreshBtnTxt(): string {
      return "Relancer la synchronisation";
    },
    reportBtnTxt(): string {
      return "Afficher le rapport";
    },
    canContinueAfterImport(): boolean {
      return !!this.synchro98?.synchroDocuments || !!this.synchro98?.synchroCompleted;
    },
    canReportActions(): boolean {
      return !isProduction() && this.internalStep === 0;
    },
    refreshDisabled(): boolean {
      return !this.synchro98?.synchroCompleted;
    },
    projetGroupBy(): Synchro98ProjetGroupBys {
      return this.selectedProjetGroupBy as Synchro98ProjetGroupBys;
    },
    projetGroupByNoneSelected(): boolean {
      return this.projetGroupBy === Synchro98ProjetGroupBys.NONE;
    },
    projetGroupByMonthSelected(): boolean {
      return this.projetGroupBy === Synchro98ProjetGroupBys.MONTH;
    },
    projetGroupByYearSelected(): boolean {
      return this.projetGroupBy === Synchro98ProjetGroupBys.YEAR;
    },
    projectOrganizationTitle(): string {
      return RessourceAppCustom(AppCustoms.ONBOARDING_PROJECT_ORGANIZATION_TITLE).label.replace("$ANNEE", (new Date().getFullYear()-1).toString());
    },
    monthImage(): string {
      return MonthImage;
    },
    numeroObjetImage(): string {
      return NumeroObjetImage;
    },
    yearImage(): string {
      return YearImage;
    },
    noneLabel(): string {
      return RessourceAppCustom(AppCustoms.ONBOARDING_PROJECT_ORGANIZATION_NONE).label;
    },
    monthLabel(): string {
      return RessourceAppCustom(AppCustoms.ONBOARDING_PROJECT_ORGANIZATION_MONTH).label;
    },
    yearLabel(): string {
      return RessourceAppCustom(AppCustoms.ONBOARDING_PROJECT_ORGANIZATION_YEAR).label;
    },
    nextDisabled(): boolean {
      if (this.internalStep === 0) {
        return !this.canContinueAfterImport;
      }
      return false;
    },
    previousDisabled(): boolean {
      return !this.canContinueAfterImport;
    },
    progresses(): OnboardingProgressDisplay[] {
      return (this.synchro98?.progresses || []).map((progress, index) => ({
        ...progress,
        id: progress.label || index,
        image: ProgressCardDocument,
      }));
    },
  },
  methods: {
    startImport(refresh: boolean): void {
      if (!this.synchro98 || this.synchro98.id <= 0) {
        return;
      }
      if (!refresh && this.canContinueAfterImport) {
        return;
      }
      if (!refresh && this.userId === this.synchro98.id) {
        return;
      }
      this.userId = this.synchro98.id;
      this.synchro98.refresh = refresh;
      if (refresh) {
        this.synchro98.progresses = [];
        this.synchro98.synchroDocuments = false;
        this.synchro98.synchroCompleted = false;
      }
      this.registerDialog();
      Synchro98.import(this.synchro98);
    },
    registerDialog(): void {
      if (this.dialogRegistered) {
        return;
      }
      this.unregisterDialog = DialogHubManager.waitQuestion(this.showDialog);
      this.dialogRegistered = true;
    },
    showDialog(dialog: Dialog): void {
      const section = new Section();
      section.component = SectionDialog;
      section.type = "dialogue";
      section.data = dialog;
      section.showButton = true;
      this.dialogSection = section;
      this.dialogOpen = true;
    },
    closeDialog(): void {
      this.dialogOpen = false;
      this.dialogSection = null;
      this.$emit("failed");
    },
    refreshImport(): void {
      this.startImport(true);
    },
    showReport(): void {
      if (this.synchro98) {
        Synchro98.showReport(this.synchro98);
      }
    },
    previous(): void {
      if (this.previousDisabled) {
        return;
      }
      if (this.internalStep === 1) {
        this.internalStep = 0;
      } else {
        this.$emit("previous");
      }
    },
    next(): void {
      if (this.internalStep === 0) {
        this.internalStep = 1;
      } else {
        if (this.synchro98) {
          this.synchro98.projetGroupBy = this.selectedProjetGroupBy;
          Synchro98.setProjects(this.synchro98);
        }
        this.$emit("next", this.synchro98);
      }
    },
    setProjetGroupByNone(): void {
      const value = Synchro98ProjetGroupBys.NONE;
      if (this.synchro98) {
        this.synchro98.projetGroupBy = value;
      }
      this.$emit("updateProjetGroupBy", value);
    },
    setProjetGroupByMonth(): void {
      const value = Synchro98ProjetGroupBys.MONTH;
      if (this.synchro98) {
        this.synchro98.projetGroupBy = value;
      }
      this.$emit("updateProjetGroupBy", value);
    },
    setProjetGroupByYear(): void {
      const value = Synchro98ProjetGroupBys.YEAR;
      if (this.synchro98) {
        this.synchro98.projetGroupBy = value;
      }
      this.$emit("updateProjetGroupBy", value);
    },
  },
});</script>

<style scoped>
  .onboarding-progress-page {
    width: 638px;
    padding-bottom: 128px;
  }

  .onboarding-progress-page__header {
    padding-top: 72px;
    padding-bottom: 44px;
    gap: 32px;
  }

  .onboarding-progress-page__image {
    width: 205px;
    height: 162px;
  }

  .onboarding-progress-page__title {
    max-width: 590px;
    color: rgb(var(--v-theme-grey20, 47, 54, 61));
    font-size: 24px;
    line-height: 1.08;
  }

  .onboarding-progress-page__table {
    width: 100%;
  }

  .onboarding-project-organization {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .onboarding-project-organization__image {
    width: 205px;
    height: 162px;
    margin-bottom: 52px;
  }

  .onboarding-project-organization__title {
    width: 638px;
    max-width: 100%;
    margin-bottom: 48px;
    color: rgb(var(--v-theme-grey20, 47, 54, 61));
    font-size: 24px;
    line-height: 1.08;
  }

  .onboarding-project-organization__cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    width: 638px;
    max-width: 100%;
  }

  .onboarding-project-organization-card {
    min-height: 164px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border: 1px solid rgb(var(--v-theme-grey90, 225, 227, 229));
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    text-align: left;
  }

  .onboarding-project-organization-card--selected {
    background: #e9fbfd;
    border-color: #b9ecef;
  }

  .onboarding-project-organization-card__radio {
    width: 14px;
    height: 14px;
    margin-bottom: 28px;
    position: relative;
    display: block;
    border: 1px solid #ffd4d4;
    border-radius: 50%;
    background: #ffffff;
  }

  .onboarding-project-organization-card__radio::after {
    content: "";
    width: 6px;
    height: 6px;
    position: absolute;
    top: 3px;
    left: 3px;
    border-radius: 50%;
    background: transparent;
  }

  .onboarding-project-organization-card__radio--selected {
    border-color: rgb(var(--v-theme-secondary50, 82, 112, 113));
  }

  .onboarding-project-organization-card__radio--selected::after {
    background: rgb(var(--v-theme-secondary50, 82, 112, 113));
  }

  .onboarding-project-organization-card__icon {
    width: 58px;
    height: 46px;
    margin-bottom: 16px;
    object-fit: contain;
  }

  .onboarding-project-organization-card__label {
    color: rgb(var(--v-theme-secondary20, 19, 82, 83));
    font-size: 13px;
    font-weight: 500;
    line-height: 1.25;
  }

  .onboarding-progress-page__footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .onboarding-progress-page__report-actions,
  .onboarding-progress-page__nav-actions {
    display: flex;
    gap: 8px;
  }

    .onboarding-progress-page__footer :deep(.v-btn) {
      width: 262px;
    }

  @media (max-width: 960px) {
    .onboarding-progress-page {
      width: 100%;
    }
  }

  @media (max-width: 600px) {
    .onboarding-progress-page {
      padding-bottom: 32px;
    }

    .onboarding-progress-page__header {
      padding-top: 24px;
    }

    .onboarding-progress-page__footer :deep(.v-btn) {
      width: 100%;
    }

    .onboarding-progress-page__report-actions,
    .onboarding-progress-page__nav-actions {
      width: 100%;
      flex-direction: column;
    }
  }
</style>
